import { dehydrate, QueryClient } from "@tanstack/react-query";

interface SSRQueryFetcher<TData> {
  key: readonly unknown[];
  fn: () => Promise<TData>;
  staleTime?: number; // Batch 4: 支持自定义缓存时间
  gcTime?: number; // Batch 4: 支持自定义GC时间
}

interface WithSSRQueryParams {
  fetchers: SSRQueryFetcher<unknown>[];
}

interface WithSSRQueryResult {
  queryClient: QueryClient;
  dehydratedState: ReturnType<typeof dehydrate>;
  getInitialData<T>(key: readonly unknown[]): T | undefined;
}

export const getQueryClient = () => new QueryClient();
export { dehydrate };

/**
 * Batch 4优化: 支持自定义缓存策略的SSR查询
 * - 为不同数据设置差异化缓存时间
 * - 减少服务端重复请求，提升TTFB
 */
export const withSSRQuery = async ({
  fetchers,
}: WithSSRQueryParams): Promise<WithSSRQueryResult> => {
  const queryClient = getQueryClient();

  await Promise.all(
    fetchers.map(({ key, fn, staleTime, gcTime }) =>
      queryClient.prefetchQuery({
        queryKey: key,
        queryFn: fn,
        staleTime: staleTime, // Batch 4: 使用自定义缓存时间
        gcTime: gcTime, // Batch 4: 使用自定义GC时间
      }),
    ),
  );

  const dehydratedState = dehydrate(queryClient);

  return {
    queryClient,
    dehydratedState,
    getInitialData: <T>(key: readonly unknown[]) =>
      queryClient.getQueryData<T>(key),
  };
};
